import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Spinner,
  DataTable,
  Divider,
} from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import {
  getMapViolations,
  getMapCoverage,
  getMapChanges,
  scanStorefrontMap,
  runMapShopifySync,
  type MapChangeRow,
  type MapSyncSummary,
} from '../api/map-compliance';
import { Button, Spinner as PSpinner, TextField, Modal } from '@shopify/polaris';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { listWheels, updateWheel } from '../api/wheels';

function fmtCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function changeBadge(t: MapChangeRow['change_type']) {
  if (t === 'added') return <Badge tone="success">Added</Badge>;
  if (t === 'removed') return <Badge tone="critical">Removed</Badge>;
  return <Badge tone="warning">Changed</Badge>;
}

export function MapCompliance() {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ wheel_id?: number; sku: string; current_map_cents: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const qc = useQueryClient();
  const violations = useQuery({
    queryKey: ['map-violations'],
    queryFn: getMapViolations,
    refetchInterval: 60_000,
  });
  const coverage = useQuery({
    queryKey: ['map-coverage'],
    queryFn: getMapCoverage,
    refetchInterval: 60_000,
  });
  const changes = useQuery({
    queryKey: ['map-changes', 7],
    queryFn: () => getMapChanges(7),
    refetchInterval: 60_000,
  });

  if (violations.isLoading || coverage.isLoading) {
    return <Page title="MAP Compliance"><Spinner /></Page>;
  }
  if (violations.error) {
    return <Page title="MAP Compliance">
      <Banner tone="critical">{(violations.error as Error).message}</Banner>
    </Page>;
  }

  const vCount = violations.data?.count ?? 0;
  const filteredViolations = (violations.data?.violations ?? [])
    .filter((v) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return v.sku.toLowerCase().includes(q)
          || (v.brand ?? '').toLowerCase().includes(q)
          || v.source_guide.toLowerCase().includes(q);
    });
  const violationRows = filteredViolations.slice(0, 100).map((v) => [
    v.sku,
    v.source_guide,
    v.brand ?? '—',
    fmtCents(v.map_price_cents),
    fmtCents(v.effective_price_cents),
    fmtCents(v.gap_cents),
    `${v.pct_below.toFixed(1)}% below`,
    <Button
      size="micro"
      onClick={() => {
        setEditing({ sku: v.sku, current_map_cents: v.map_price_cents });
        setEditValue((v.map_price_cents / 100).toFixed(2));
      }}
    >Edit MAP</Button>,
  ]);
  const coverageRows = (coverage.data?.coverage ?? []).map((c) => [
    c.source_guide,
    c.total.toLocaleString(),
    `${c.has_map.toLocaleString()} (${c.map_coverage_pct.toFixed(1)}%)`,
    c.no_map.toLocaleString(),
    c.violations === 0
      ? <Badge tone="success">0 violations</Badge>
      : <Badge tone="critical">{`${c.violations} violations`}</Badge>,
  ]);
  const changeRows = (changes.data?.changes ?? []).slice(0, 100).map((c) => [
    c.sku,
    c.source_guide,
    c.brand ?? '—',
    changeBadge(c.change_type),
    fmtCents(c.prior_map_cents),
    fmtCents(c.current_map_cents),
    c.delta_cents != null
      ? (c.delta_cents > 0 ? `+${fmtCents(c.delta_cents)}` : fmtCents(c.delta_cents))
      : '—',
  ]);

  const mapSyncDryRun = useMutation<MapSyncSummary, Error, void>({
    mutationFn: () => runMapShopifySync(false),
  });
  const mapSyncApply = useMutation<MapSyncSummary, Error, void>({
    mutationFn: () => runMapShopifySync(true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['map-violations'] });
      qc.invalidateQueries({ queryKey: ['map-coverage'] });
    },
  });

  function describeSync(s: MapSyncSummary): string {
    const head = s.dry_run
      ? `DRY RUN — ${s.drift_detected} variants would be updated`
      : `APPLIED — ${s.applied} variants updated, ${s.failed} failed`;
    const vendors = Object.entries(s.by_vendor)
      .sort((a, b) => b[1].drift - a[1].drift)
      .map(([v, n]) => `  ${v}: drift=${n.drift} applied=${n.applied} failed=${n.failed}`)
      .join('\n');
    const sample = s.changes.slice(0, 8)
      .map((c) => `  ${c.sku} ${c.source_guide}: ${fmtCents(c.shopify_price_cents_before)} → ${fmtCents(c.shopify_price_cents_after)}${c.floored_to_map ? ' (floored to MAP)' : ''}${c.error ? ` — ERROR: ${c.error}` : ''}`)
      .join('\n');
    return `${head}\n\nScanned: ${s.scanned}  Queried: ${s.queried}  Unchanged: ${s.unchanged}\nFloored to MAP: ${s.floored_to_map_count}\nDuration: ${(s.duration_ms / 1000).toFixed(1)}s\n\nBy vendor:\n${vendors || '  (none)'}\n\nFirst changes:\n${sample || '  (none)'}${s.truncated ? '\n\n(more — see Imports → job ' + s.importJobId + ')' : ''}`;
  }

  return (
    <Page
      title="MAP Compliance"
      subtitle="Minimum Advertised Price audit — violations, coverage, and recent changes per vendor"
      secondaryActions={[
        {
          content: mapSyncDryRun.isPending ? 'Scanning…' : 'Preview Shopify price sync (dry run)',
          loading: mapSyncDryRun.isPending,
          onAction: () => {
            mapSyncDryRun.mutate(undefined, {
              onSuccess: (r) => alert(describeSync(r)),
              onError: (e) => alert(`Dry run failed: ${e.message}`),
            });
          },
        },
        {
          content: mapSyncApply.isPending ? 'Applying…' : 'Push prices to Shopify',
          loading: mapSyncApply.isPending,
          destructive: true,
          onAction: () => {
            if (!confirm(
              'Push catalog effective_price_cents (with MAP floor) to every Shopify variant where the live price differs?\n\n' +
              'This MUTATES live Shopify product prices. Run the dry-run preview first.'
            )) return;
            mapSyncApply.mutate(undefined, {
              onSuccess: (r) => alert(describeSync(r)),
              onError: (e) => alert(`Sync failed: ${e.message}`),
            });
          },
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {/* Headline status */}
            <Card>
              <BlockStack gap="300">
                <TextField
                  label="Search violations by SKU or brand"
                  value={search}
                  onChange={setSearch}
                  autoComplete="off"
                  placeholder="Type a SKU, brand name, or vendor..."
                  clearButton
                  onClearButtonClick={() => setSearch('')}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">Current violations</Text>
                  {vCount === 0
                    ? <Badge tone="success">All compliant</Badge>
                    : <Badge tone="critical">{`${vCount} violation${vCount === 1 ? '' : 's'}`}</Badge>}
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Wheels currently advertised below the manufacturer's MAP
                  (effective price &lt; map_price_cents).
                </Text>
              </BlockStack>
            </Card>

            {/* Coverage by vendor */}
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">MAP coverage by vendor</Text>
                <DataTable
                  columnContentTypes={['text', 'numeric', 'text', 'numeric', 'text']}
                  headings={['Vendor', 'Total wheels', 'Has MAP', 'No MAP', 'Violations']}
                  rows={coverageRows}
                />
              </BlockStack>
            </Card>

            {/* Violations table */}
            {vCount > 0 && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">Active violations (top 100)</Text>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                    headings={['SKU', 'Vendor', 'Brand', 'MAP', 'Our price', 'Gap', '% below', 'Action']}
                    rows={violationRows}
                  />
                </BlockStack>
              </Card>
            )}

            {/* Shopify storefront MAP scan — the meaningful audit */}
            <StorefrontScanCard />

            <Divider />

            {/* MAP changes (added/removed/changed in last 7 days) */}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">MAP changes — last 7 days</Text>
                  {changes.data?.note && <Badge tone="attention">Snapshots not yet capturing</Badge>}
                </InlineStack>
                {changes.data?.note && (
                  <Banner tone="info">
                    <Text as="p" variant="bodySm">{changes.data.note}</Text>
                  </Banner>
                )}
                {!changes.data?.note && changeRows.length === 0 && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    No MAP changes in the last 7 days. Vendors haven't adjusted MAP policy
                    or removed MAP from any products.
                  </Text>
                )}
                {changeRows.length > 0 && (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric']}
                    headings={['SKU', 'Vendor', 'Brand', 'Type', 'Prior MAP', 'Current MAP', 'Delta']}
                    rows={changeRows}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
      <MapEditModal
        editing={editing}
        editValue={editValue}
        setEditValue={setEditValue}
        onClose={() => setEditing(null)}
        onSave={async () => {
          if (!editing) return;
          const newCents = Math.round(parseFloat(editValue) * 100);
          if (!Number.isFinite(newCents) || newCents <= 0) {
            alert('Enter a valid MAP price > 0');
            return;
          }
          // Need wheel id; lookup by SKU not exposed here, so we'll PATCH via SKU
          // through a wheels-by-sku helper. For now, the violations endpoint should
          // include shopify_variant_id or wheel id — adding a SKU-based PATCH path.
          try {
            // The /admin/wheels endpoint is by ID. Violations only carry SKU,
            // so look the wheel up by SKU first. Was: raw fetch('/api/admin/...
            // wheels?q=...') — wrong path (no API_BASE), no auth, never worked
            // when admin-ui is iframed. Routed through listWheels() which uses
            // apiFetch + the auto-401-refresh.
            const list = await listWheels({ q: editing.sku, limit: 1 });
            const wheelId = list?.data?.[0]?.id;
            if (!wheelId) {
              alert(`Wheel ${editing.sku} not found in admin index`);
              return;
            }
            await updateWheel(wheelId, { map_price_cents: newCents } as any);
            qc.invalidateQueries({ queryKey: ['map-violations'] });
            qc.invalidateQueries({ queryKey: ['map-coverage'] });
            setEditing(null);
          } catch (err) {
            alert(`Failed: ${(err as Error).message}`);
          }
        }}
      />
    </Page>
  );
}

function MapEditModal({
  editing,
  editValue,
  setEditValue,
  onClose,
  onSave,
}: {
  editing: { sku: string; current_map_cents: number } | null;
  editValue: string;
  setEditValue: (v: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  if (!editing) return null;
  return (
    <Modal
      open={!!editing}
      onClose={onClose}
      title={`Edit MAP for ${editing.sku}`}
      primaryAction={{ content: 'Save', onAction: onSave }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="200">
          <Text as="p" variant="bodySm">
            Current MAP: <strong>{`$${(editing.current_map_cents / 100).toFixed(2)}`}</strong>
          </Text>
          <TextField
            label="New MAP price (USD)"
            type="number"
            step={0.01}
            min={0}
            value={editValue}
            onChange={setEditValue}
            autoComplete="off"
            prefix="$"
          />
          <Text as="p" variant="bodySm" tone="subdued">
            Updates the canonical MAP value in our DB. When products are pushed to
            Shopify, this MAP is included in the variant tags + price gating. No
            Shopify push happens from this edit.
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

function fmtCents2(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StorefrontScanCard() {
  const m = useMutation({ mutationFn: scanStorefrontMap });
  const r = m.data;
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd">Shopify storefront scan</Text>
            <Text as="span" variant="bodySm" tone="subdued">
              Queries Shopify Admin API for live variant prices. THIS is the audit
              that catches real violations (sale collections, manual variant edits, etc.).
            </Text>
          </BlockStack>
          <Button
            variant="primary"
            loading={m.isPending}
            onClick={() => m.mutate()}
          >
            {m.isPending ? 'Scanning…' : 'Run scan now'}
          </Button>
        </InlineStack>

        {m.isPending && <PSpinner size="small" />}

        {m.error && (
          <Banner tone="critical">
            <Text as="p" variant="bodySm">
              Scan failed: {(m.error as Error).message}
            </Text>
          </Banner>
        )}

        {r && r.note && (
          <Banner tone="info">
            <Text as="p" variant="bodySm">{r.note}</Text>
          </Banner>
        )}

        {r && !r.note && (
          <BlockStack gap="200">
            <InlineStack gap="400">
              <Text as="span" variant="bodySm" tone="subdued">
                Scanned: {r.scanned.toLocaleString()} · Queried: {r.queried.toLocaleString()} ·
                Took: {r.duration_ms}ms
              </Text>
              {r.violations.length === 0
                ? <Badge tone="success">All compliant</Badge>
                : <Badge tone="critical">{`${r.violations.length} violations`}</Badge>}
            </InlineStack>

            {r.violations.length > 0 && (
              <DataTable
                columnContentTypes={['text','text','text','numeric','numeric','numeric','text']}
                headings={['SKU','Vendor','Brand','MAP','Shopify price','Gap','% below']}
                rows={r.violations.slice(0, 100).map(v => [
                  v.sku,
                  v.source_guide,
                  v.brand ?? '—',
                  fmtCents2(v.map_price_cents),
                  fmtCents2(v.shopify_price_cents),
                  fmtCents2(v.gap_cents),
                  `${v.pct_below.toFixed(1)}% below`,
                ])}
              />
            )}

            {r.not_found_in_shopify.length > 0 && (
              <Banner tone="warning">
                <Text as="p" variant="bodySm">
                  {r.not_found_in_shopify.length} variant ID(s) referenced in our DB
                  weren't found in Shopify (deleted variants, wrong store, or stale IDs).
                </Text>
              </Banner>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

